<template>
  <div>
    <!-- スライドイン表示の場合 -->
    <transition :name="`slide-${slideDirection}`" v-if="slideDirection">
      <div v-if="isShowModalSync">
        <!-- オーバーレイ -->
        <div class="slide-modal-overlay" @click="handleOverlayClick" v-if="!noOverlay"></div>
        <!-- モーダルコンテンツ -->
        <div 
          class="slide-modal-content"
          :class="[`slide-${slideDirection}`, { 'draggable': draggable, 'no-overlay': noOverlay }]"
          :style="getModalStyle"
          ref="modalContent">
          <div class="card -card">
            <header class="card-header -header" v-if="title"
              @mousedown="startDrag"
              :class="{ 'drag-handle': draggable }">
              <p class="card-header-title title">
                {{title}}
              </p>
            </header>
            <slot></slot>
          </div>
        </div>
      </div>
    </transition>
    <!-- 通常のモーダル表示 -->
    <b-modal
      v-else
      :width="width"
      :active.sync="isShowModalSync"
      :on-cancel="cancel"
      :can-cancel="canCancel">
      <div class="card -card">
        <header class="card-header -header" v-if="title">
          <p class="card-header-title title">
            {{title}}
          </p>
        </header>
        <slot></slot>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'BasicModal',
  componentName: 'BasicModal',
  props: {
    title: {
      type: String,
      required: false
    },
    isShowModal: {
      type: Boolean,
      required: false,
      default: false
    },
    canCancel: {
      type: Boolean
    },
    width: {
      type: String,
      'default': () => ('680px')
    },
    slideDirection: {
      type: String,
      default: null,
      validator: value => [null, 'right', 'left', 'top', 'bottom'].includes(value)
    },
    slideWidth: {
      type: String,
      default: '50%'
    },
    slideMaxWidth: {
      type: String,
      default: '500px'
    },
    noOverlay: {
      type: Boolean,
      default: false
    },
    draggable: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    isShowModal () {
      this.isShowModalSync = this.isShowModal
    }
  },
  data () {
    return {
      isShowModalSync: this.isShowModal,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      modalX: 0,
      modalY: 0
    }
  },
  computed: {
    getSlideStyle() {
      if (this.slideDirection === 'right' || this.slideDirection === 'left') {
        return {
          width: this.slideWidth,
          maxWidth: this.slideMaxWidth,
          height: '100%'
        }
      } else if (this.slideDirection === 'top' || this.slideDirection === 'bottom') {
        return {
          height: this.slideWidth,
          maxHeight: this.slideMaxWidth,
          width: '100%'
        }
      }
      return {}
    },
    getModalStyle() {
      const baseStyle = this.getSlideStyle
      if (this.draggable && this.slideDirection) {
        return {
          ...baseStyle,
          transform: `translate(${this.modalX}px, ${this.modalY}px)`
        }
      }
      return baseStyle
    }
  },
  methods: {
    cancel () {
      this.$emit('cancel')
    },
    handleOverlayClick() {
      if (this.canCancel) {
        this.cancel()
      }
    },
    handleOutsideClick(event) {
      if (!this.canCancel || !this.slideDirection || !this.noOverlay) return
      
      const modalContent = this.$refs.modalContent
      if (modalContent && !modalContent.contains(event.target)) {
        this.cancel()
      }
    },
    handleEscKey(event) {
      if (event.key === 'Escape' && this.canCancel && this.slideDirection) {
        this.cancel()
      }
    },
    startDrag(e) {
      if (!this.draggable) return
      this.isDragging = true
      this.dragStartX = e.clientX - this.modalX
      this.dragStartY = e.clientY - this.modalY
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
      e.preventDefault()
    },
    onDrag(e) {
      if (!this.isDragging) return
      this.modalX = e.clientX - this.dragStartX
      this.modalY = e.clientY - this.dragStartY
    },
    stopDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
    }
  },
  mounted() {
    if (this.slideDirection) {
      document.addEventListener('keydown', this.handleEscKey)
      if (this.noOverlay && this.canCancel) {
        setTimeout(() => {
          document.addEventListener('click', this.handleOutsideClick)
        }, 100)
      }
    }
  },
  beforeDestroy() {
    if (this.slideDirection) {
      document.removeEventListener('keydown', this.handleEscKey)
      if (this.noOverlay && this.canCancel) {
        document.removeEventListener('click', this.handleOutsideClick)
      }
    }
    if (this.isDragging) {
      this.stopDrag()
    }
  }
}
</script>

<style lang="scss" scoped>
.-card {
  width: 100%;
  border-radius: 8px;
}
.-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  p {
    display: block;
    width: calc(100% - 30px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .delete {
    cursor: pointer;
  }
  
  .close-btn {
    margin-left: auto;
    background: #f5f5f5;
    border: 1px solid #ddd;
    color: #666;
    
    &:hover {
      background: #e0e0e0;
      border-color: #999;
      color: #333;
    }
  }
}

// スライドイン用のスタイル
.slide-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
}

.slide-modal-content {
  position: fixed;
  background: #FEFEFE;
  z-index: 201;
  overflow-y: auto;
  padding: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  
  &.slide-right {
    top: 0;
    right: 0;
  }
  
  &.slide-left {
    top: 0;
    left: 0;
  }
  
  &.slide-top {
    top: 0;
    left: 0;
  }
  
  &.slide-bottom {
    bottom: 0;
    left: 0;
  }
  
  &.no-overlay {
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    border: 1px solid #ddd;
  }
  
  &.draggable {
    .drag-handle {
      cursor: move;
      user-select: none;
      
      &:active {
        cursor: grabbing;
      }
    }
  }
}

// トランジションアニメーション
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 225ms cubic-bezier(0, 0, 0.2, 1);
}

.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 225ms cubic-bezier(0, 0, 0.2, 1);
}

.slide-left-enter,
.slide-left-leave-to {
  transform: translateX(-100%);
}

.slide-top-enter-active,
.slide-top-leave-active {
  transition: transform 225ms cubic-bezier(0, 0, 0.2, 1);
}

.slide-top-enter,
.slide-top-leave-to {
  transform: translateY(-100%);
}

.slide-bottom-enter-active,
.slide-bottom-leave-active {
  transition: transform 225ms cubic-bezier(0, 0, 0.2, 1);
}

.slide-bottom-enter,
.slide-bottom-leave-to {
  transform: translateY(100%);
}
</style>



// WEBPACK FOOTER //
// src/components/Modals/BasicModal.vue